import React from 'react'
import {
  DcButton,
  DcDropDown,
  DcInput,
  PixelFactoryContext,
  PixelTable,
} from 'pixel-base'
import {
  createPartner,
  deletePartner,
  getPartners,
  updatePartner,
} from 'utils/api-routes-accounts'
import FinanceListActions from '../../components/domain/FinanceListActions'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceStatusBadge from '../../components/FinanceStatusBadge'
import FinanceEntityDrawer from '../../components/foundation/FinanceEntityDrawer'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { Partner } from '../../types/finance'

const defaultForm = {
  id: null,
  code: '',
  name: '',
  partner_type: 'customer',
  currency_code: 'PKR',
  email: '',
  phone: '',
}

const Partners = () => {
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const partners = useFinanceObservable<Partner[]>(() => getPartners())
  const [isOpen, setIsOpen] = React.useState(false)
  const [form, setForm] = React.useState<any>(defaultForm)

  const reset = () => {
    setIsOpen(false)
    setForm(defaultForm)
  }

  const save = () => {
    const request = form.id ? updatePartner(form.id, form) : createPartner(form)
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(
          form.id ? 'Partner updated' : 'Partner created',
        )
        reset()
        partners.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Failed to save partner'),
    })
  }

  const remove = (id: number) => {
    deletePartner(id).subscribe({
      next: () => {
        pixelFactory?.showSuccess?.('Partner deleted')
        partners.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Unable to delete partner'),
    })
  }

  return (
    <>
      <FinancePageShell
        title="Partners"
        isLoading={partners.loading}
        primaryAction={{ label: 'Add Partner', onClick: () => setIsOpen(true) }}
      >
        <PixelTable
          columns={[
            { name: 'Code', selector: (row) => row.code },
            { name: 'Name', selector: (row) => row.name },
            { name: 'Type', selector: (row) => row.partner_type },
            { name: 'Currency', selector: (row) => row.currency_code || 'PKR' },
            {
              name: 'Status',
              selector: (row) => (
                <FinanceStatusBadge status={row.is_active ?? true} />
              ),
            },
            {
              name: 'Actions',
              selector: (row) => (
                <FinanceListActions
                  actions={[
                    {
                      key: 'edit',
                      label: 'Edit',
                      tone: 'secondary',
                      onClick: () => {
                        setForm(row)
                        setIsOpen(true)
                      },
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      tone: 'danger',
                      onClick: () => remove(row.id),
                    },
                  ]}
                />
              ),
            },
          ]}
          data={partners.data || []}
          searchable
          pagination
        />
      </FinancePageShell>
      <FinanceEntityDrawer
        isOpen={isOpen}
        onClose={reset}
        title={form.id ? 'Edit Partner' : 'Create Partner'}
        width="32%"
      >
        <DcInput
          value={form.code}
          placeholder="Code"
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />
        <DcInput
          value={form.name}
          placeholder="Name"
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <DcDropDown
          value={form.partner_type}
          options={[
            { value: 'customer', label: 'Customer' },
            { value: 'vendor', label: 'Vendor' },
            { value: 'both', label: 'Both' },
            { value: 'employee', label: 'Employee' },
            { value: 'other', label: 'Other' },
          ]}
          onChange={(e) => setForm({ ...form, partner_type: e.target.value })}
        />
        <DcInput
          value={form.currency_code}
          placeholder="Currency"
          onChange={(e) => setForm({ ...form, currency_code: e.target.value })}
        />
        <DcInput
          value={form.email}
          placeholder="Email"
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <DcInput
          value={form.phone}
          placeholder="Phone"
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <DcButton onClick={save}>Save Partner</DcButton>
      </FinanceEntityDrawer>
    </>
  )
}

export default Partners
